import { useEffect, useMemo, useState } from 'react';
import { useQueryWithStore, useDataProvider } from 'react-admin';

const limitToCall = 50;

const useUserNotificationsDataProvider = props => {
  const dataProvider = useDataProvider();
  const [fundsData, setFundsData] = useState({ data: [] });
  const [investorsData, setInvestorsData] = useState({ data: [] });
  const [fundInvestorsData, setFundInvestorsData] = useState({ data: [] });

  const [fundIdsIn, investorIdsIn] = useMemo(() => {
    const _fundIds = {};
    const _investorIds = {};
    props.notifications &&
      Object.values(props.notifications).forEach(notif => {
        if (notif.fundId && notif.fundId !== '*') {
          _fundIds[notif.fundId] = true;
        }
        if (notif.investorId && notif.investorId !== '*') {
          _investorIds[notif.investorId] = true;
        }
      });
    return [Object.keys(_fundIds), Object.keys(_investorIds)];
  }, [props.notifications]);

  const { data: authorizations = [] } = useQueryWithStore({
    type: 'GET_LIST',
    resource: 'userauthorizations',
    payload: {
      filter: { userId: props.id }
    }
  });

  const {
    fundIds,
    investorIds,
    fundsWithAllInvestorsAuthorization
  } = useMemo(() => {
    const _fundIds = {};
    const _investorIds = {};
    const _fundsWithAllInvestorsAuthorization = {};
    authorizations.forEach(authorization => {
      _fundIds[authorization.fundId] = true;
      _investorIds[authorization.investorId] = true;
      authorization.investorId === '*' &&
        (_fundsWithAllInvestorsAuthorization[authorization.fundId] = true);
    });
    return {
      fundIds: Object.keys(_fundIds),
      investorIds: Object.keys(_investorIds),
      fundsWithAllInvestorsAuthorization: _fundsWithAllInvestorsAuthorization
    };
  }, [authorizations]);

  useEffect(() => {
    if (fundIdsIn.length) {
      setFundsData(data => ({ ...data, loading: true }));
      dataProvider
        .getList('funds', {
          filter: { id: `[${fundIdsIn}]` }
        })
        .then(data => {
          setFundsData(data);
        });
    }
  }, [fundIdsIn, dataProvider]);

  useEffect(() => {
    if (investorIdsIn.length) {
      setInvestorsData(data => ({ ...data, loading: true }));
      dataProvider
        .getList('investors', {
          filter: { id: `[${investorIdsIn}]` }
        })
        .then(data => {
          setInvestorsData(data);
        });
    }
  }, [investorIdsIn, dataProvider]);

  useEffect(() => {
    if (fundIds.length) {
      setFundInvestorsData(data => ({ ...data, loading: true }));
      const payload = {};
      if (fundIds.length <= limitToCall) {
        payload.filter = { fundId: `[${fundIds}]` };
      }

      dataProvider.getList('fundinvestors', payload).then(data => {
        const {
          loading: isLoadingFundsInvestors,
          data: _fundsInvestors = [],
          error: errorFundsInvestors
        } = data;
        let fundInvestors = [];
        if (
          !isLoadingFundsInvestors &&
          !errorFundsInvestors &&
          _fundsInvestors.length
        ) {
          if (fundIds.length > limitToCall) {
            fundInvestors = _fundsInvestors.filter(
              item =>
                fundIds.includes(item.fundId) &&
                (investorIds.includes(item.investorId) ||
                  fundsWithAllInvestorsAuthorization[item.fundId])
            );
          } else {
            fundInvestors = _fundsInvestors.filter(
              item =>
                investorIds.includes(item.investorId) ||
                fundsWithAllInvestorsAuthorization[item.fundId]
            );
          }
        }
        data.data = fundInvestors;
        setFundInvestorsData(data);
      });
    }
  }, [fundIds, dataProvider, investorIds, fundsWithAllInvestorsAuthorization]);

  return {
    fundInvestors: fundInvestorsData,
    funds: fundsData,
    investors: investorsData
  };
};

export default useUserNotificationsDataProvider;
